import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DatePicker from '../DatePicker';
import ChartCard from '../charts/ChartCard';
import { ChatDashboardTabProps } from '../../types';
import {
  getNonRelevantQuestions,
  getQuestionAnalysisAll,
  getQuestionAnalysisCounts,
  getWebSearchQuestions,
  NonRelevantQuestionItem,
  QuestionAnalysisAllResponse,
  QuestionAnalysisCountsDailyStat,
  WebSearchQuestionItem,
} from '../../utils/dashboardApi';
import { buildRelatedQuestionsDataset, RelatedQuestionsDataset } from '../../utils/questionAnalysisCounts';
import { GENERAL_RATE_TOOLTIPS, QUESTION_ANALYSIS_TOOLTIPS } from '../../utils/tooltips';
import { buildCsvContent, downloadCsv } from '../../utils/exportUtils';
import {
  buildMessagesCsvSpec,
  buildNonRelevantCsvSpec,
  buildWebSearchCsvSpec,
} from '../../utils/exportConfigs';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const NON_RELEVANT_LIMIT = 100;
const WEB_SEARCH_LIMIT = 100;

const QuestionAnalysisTab: React.FC<ChatDashboardTabProps> = ({ dateRange, onDateRangeChange }) => {
  const [nonRelevantQuestions, setNonRelevantQuestions] = useState<NonRelevantQuestionItem[]>([]);
  const [nonRelevantLoading, setNonRelevantLoading] = useState(false);
  const [nonRelevantError, setNonRelevantError] = useState<string | null>(null);
  const [webSearchQuestions, setWebSearchQuestions] = useState<WebSearchQuestionItem[]>([]);
  const [webSearchLoading, setWebSearchLoading] = useState(false);
  const [webSearchError, setWebSearchError] = useState<string | null>(null);
  const [relatedQuestionsData, setRelatedQuestionsData] = useState<RelatedQuestionsDataset>({
    labels: [],
    values: [],
  });
  const [rawRelatedStats, setRawRelatedStats] = useState<QuestionAnalysisCountsDailyStat[]>([]);
  const [generalStats, setGeneralStats] = useState<QuestionAnalysisAllResponse['data'] | null>(null);
  const [generalStatsLoading, setGeneralStatsLoading] = useState(false);
  const [generalStatsError, setGeneralStatsError] = useState<string | null>(null);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);

  const buildCsvFilename = useCallback(
    (prefix: string) => {
      const startLabel = (dateRange.startDate ?? 'start').replace(/:/g, '-');
      const endLabel = (dateRange.endDate ?? 'end').replace(/:/g, '-');
      return `${prefix}_${startLabel}_to_${endLabel}.csv`;
    },
    [dateRange.endDate, dateRange.startDate]
  );

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setGeneralStats(null);
      setGeneralStatsLoading(false);
      setGeneralStatsError('Please select a valid time range.');
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchGeneralStats = async () => {
      setGeneralStatsLoading(true);
      setGeneralStatsError(null);

      try {
        const response = await getQuestionAnalysisAll({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          signal: controller.signal,
        });

        if (!isMounted) return;

        setGeneralStats(response.data);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load question analysis summary:', error);
        if (isMounted) {
          setGeneralStats(null);
          setGeneralStatsError('Failed to load general rate data.');
        }
      } finally {
        if (isMounted) {
          setGeneralStatsLoading(false);
        }
      }
    };

    fetchGeneralStats();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setNonRelevantQuestions([]);
      setNonRelevantLoading(false);
      setNonRelevantError('Please select a valid time range.');
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchNonRelevant = async () => {
      setNonRelevantLoading(true);
      setNonRelevantError(null);

      try {
        const response = await getNonRelevantQuestions({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: NON_RELEVANT_LIMIT,
          signal: controller.signal,
        });

        if (!isMounted) return;
        setNonRelevantQuestions(response.data.questions);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load non-relevant questions:', error);
        if (isMounted) {
          setNonRelevantQuestions([]);
          setNonRelevantError('Failed to load non-relevant questions.');
        }
      } finally {
        if (isMounted) {
          setNonRelevantLoading(false);
        }
      }
    };

    fetchNonRelevant();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setWebSearchQuestions([]);
      setWebSearchLoading(false);
      setWebSearchError('Please select a valid time range.');
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchWebSearch = async () => {
      setWebSearchLoading(true);
      setWebSearchError(null);

      try {
        const response = await getWebSearchQuestions({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: WEB_SEARCH_LIMIT,
          signal: controller.signal,
        });

        if (!isMounted) return;
        setWebSearchQuestions(response.data.questions);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load web-search questions:', error);
        if (isMounted) {
          setWebSearchQuestions([]);
          setWebSearchError('Failed to load web-search questions.');
        }
      } finally {
        if (isMounted) {
          setWebSearchLoading(false);
        }
      }
    };

    fetchWebSearch();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setRelatedQuestionsData({ labels: [], values: [] });
      setRelatedError('Please select a valid time range.');
      setRelatedLoading(false);
      setRawRelatedStats([]);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();
    const fetchCounts = async () => {
      setRelatedLoading(true);
      setRelatedError(null);
      try {
        const response = await getQuestionAnalysisCounts({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          signal: controller.signal,
        });
        if (!isMounted) return;
        const dataset = buildRelatedQuestionsDataset(
          response.data.daily_stats,
          dateRange.startDate,
          dateRange.endDate
        );
        setRelatedQuestionsData(dataset);
        setRawRelatedStats(response.data.daily_stats ?? []);
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load question analysis data:', err);
        if (isMounted) {
          setRelatedQuestionsData({ labels: [], values: [] });
          setRelatedError('Failed to load question analysis data.');
          setRawRelatedStats([]);
        }
      } finally {
        if (isMounted) {
          setRelatedLoading(false);
        }
      }
    };

    fetchCounts();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  const generalRateItems = useMemo(
    () => {
      if (!generalStats) {
        return [];
      }
      const webSearchRatio =
        generalStats.web_search_ratio_in_related ?? generalStats.web_search_ratio ?? null;

      return [
        {
          label: 'Avg. Relevance',
          value: formatPercentLabel(normalizePercent(generalStats.sdg_relevance_ratio)),
        },
        {
          label: 'Coverage',
          value: formatPercentLabel(calculateComplementPercent(webSearchRatio)),
        },
        {
          label: 'Irrelevant',
          value: formatCountLabel(generalStats.non_related_messages),
        },
        {
          label: 'Web Search',
          value: formatCountLabel(generalStats.web_search_enabled_messages),
        },
      ];
    },
    [generalStats]
  );

  const relevanceDistributionChartData = useMemo(
    () => ({
      labels: relatedQuestionsData.labels,
      datasets: [
        {
          label: 'Questions',
          data: relatedQuestionsData.values,
          backgroundColor: '#3b82f6',
          borderRadius: 6,
        },
      ],
    }),
    [relatedQuestionsData.labels, relatedQuestionsData.values]
  );

  const relevanceDistributionOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = typeof context.parsed === 'number' ? context.parsed : context.parsed?.y;
              return `${value ?? 0} messages`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: { color: '#64748b' },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          ticks: { color: '#64748b', precision: 0 },
          grid: { color: 'rgba(148, 163, 184, 0.2)' },
        },
      },
    }),
    []
  );

  const canExportRelated = rawRelatedStats.length > 0;
  const handleExportRelated = useCallback(() => {
    if (!rawRelatedStats.length) {
      return;
    }
    const spec = buildMessagesCsvSpec(rawRelatedStats);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('related_questions'));
  }, [rawRelatedStats, buildCsvFilename]);
  const relatedCardActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton size="small" onClick={handleExportRelated} disabled={!canExportRelated}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportRelated, handleExportRelated]
  );

  const canExportNonRelevant = nonRelevantQuestions.length > 0;
  const handleExportNonRelevant = useCallback(() => {
    if (!nonRelevantQuestions.length) {
      return;
    }
    const spec = buildNonRelevantCsvSpec(nonRelevantQuestions);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('zero_relevance_questions'));
  }, [nonRelevantQuestions, buildCsvFilename]);
  const nonRelevantCardActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton
            size="small"
            onClick={handleExportNonRelevant}
            disabled={!canExportNonRelevant}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportNonRelevant, handleExportNonRelevant]
  );

  const canExportWebSearch = webSearchQuestions.length > 0;
  const handleExportWebSearch = useCallback(() => {
    if (!webSearchQuestions.length) {
      return;
    }
    const spec = buildWebSearchCsvSpec(webSearchQuestions);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('web_search_questions'));
  }, [webSearchQuestions, buildCsvFilename]);
  const webSearchCardActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton size="small" onClick={handleExportWebSearch} disabled={!canExportWebSearch}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportWebSearch, handleExportWebSearch]
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Question Analysis</h2>
        <div className="w-full sm:w-auto sm:min-w-[280px]">
          <DatePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={onDateRangeChange}
            className="w-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" style={{ gridTemplateRows: 'minmax(240px, auto) minmax(360px, auto)' }}>
        <div className="flex min-h-0 flex-col">
          <ChartCard
            title="General Rate"
            fullHeight
          >
            {generalStatsLoading && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Loading general rate...
              </div>
            )}
              {!generalStatsLoading && generalStatsError && (
                <div className="flex h-full items-center justify-center text-center text-sm text-red-500">
                  {generalStatsError}
                </div>
              )}
              {!generalStatsLoading && !generalStatsError && generalRateItems.length === 0 && (
                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                  No rate data for the selected range.
                </div>
              )}
              {!generalStatsLoading && !generalStatsError && generalRateItems.length > 0 && (
                <div className="grid h-full gap-4 sm:grid-cols-4">
                  {generalRateItems.map(({ label, value }) => (
                    <Tooltip key={label} title={GENERAL_RATE_TOOLTIPS[label] ?? label}>
                      <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-100 bg-gray-50 p-4 text-center cursor-help">
                        <span className="text-3xl font-semibold text-gray-900">{value}</span>
                        <span className="text-sm font-medium text-gray-500">{label}</span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </ChartCard>
        </div>
        <div className="flex min-h-0 flex-col">
          <ChartCard
            title="Relevance Distribution"
            fullHeight
            actions={relatedCardActions}
            tooltip={QUESTION_ANALYSIS_TOOLTIPS.relevanceDistribution}
          >
            {relatedLoading && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Loading relevance distribution...
              </div>
            )}
            {!relatedLoading && relatedError && (
              <div className="flex h-full items-center justify-center text-center text-sm text-red-500">
                {relatedError}
              </div>
            )}
            {!relatedLoading && !relatedError && relatedQuestionsData.labels.length === 0 && (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                No distribution data for the selected range.
              </div>
            )}
            {!relatedLoading && !relatedError && relatedQuestionsData.labels.length > 0 && (
              <div className="h-full min-h-0">
                <Bar data={relevanceDistributionChartData} options={relevanceDistributionOptions} />
              </div>
            )}
          </ChartCard>
        </div>
        <div className="flex min-h-0 flex-col">
          <ChartCard
            title="Zero-Relevance Questions"
            scrollable
            fullHeight
            actions={nonRelevantCardActions}
            tooltip={QUESTION_ANALYSIS_TOOLTIPS.zeroRelevance}
          >
            {nonRelevantLoading && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Loading zero-relevance questions...
              </div>
            )}
            {!nonRelevantLoading && nonRelevantError && (
              <div className="flex h-full items-center justify-center text-center text-sm text-red-500">
                {nonRelevantError}
              </div>
            )}
            {!nonRelevantLoading && !nonRelevantError && nonRelevantQuestions.length === 0 && (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                No zero-relevance questions for this range.
              </div>
            )}
            {!nonRelevantLoading && !nonRelevantError && nonRelevantQuestions.length > 0 && (
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex items-center px-1 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span className="w-32">User</span>
                  <span className="w-28">Time</span>
                  <span className="flex-1">Question</span>
                </div>
                <div
                  className="custom-scrollbar flex-1 min-h-0 overflow-y-auto pr-2"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e1 #f1f5f9',
                  }}
                >
                  {nonRelevantQuestions.map((row) => (
                    <div
                      key={row.message_id}
                      className="flex items-start gap-4 border-t border-gray-100 px-1 py-3 text-sm text-gray-700"
                    >
                      <Tooltip title={`User ID: ${row.user_id}`} arrow>
                        <span className="w-32 cursor-default font-medium text-gray-900">
                          {row.username}
                        </span>
                      </Tooltip>
                      <span className="w-28 text-xs text-gray-500">{formatDateLabel(row.question_timestamp)}</span>
                      <Tooltip title={`Message ID: ${row.message_id}`} arrow>
                        <span className="flex-1 cursor-default text-gray-600">{row.question}</span>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ChartCard>
        </div>
        <div className="flex min-h-0 flex-col">
          <ChartCard
            title="Web-Search Questions"
            scrollable
            fullHeight
            actions={webSearchCardActions}
            tooltip={QUESTION_ANALYSIS_TOOLTIPS.webSearch}
          >
            {webSearchLoading && (
              <div className="flex h-full items-center justify-center text-sm text-gray-500">
                Loading web-search questions...
              </div>
            )}
            {!webSearchLoading && webSearchError && (
              <div className="flex h-full items-center justify-center text-center text-sm text-red-500">
                {webSearchError}
              </div>
            )}
            {!webSearchLoading && !webSearchError && webSearchQuestions.length === 0 && (
              <div className="flex h-full items-center justify-center text-sm text-gray-400">
                No web-search questions for this range.
              </div>
            )}
            {!webSearchLoading && !webSearchError && webSearchQuestions.length > 0 && (
              <div className="flex h-full min-h-0 flex-col">
                <div className="flex items-center px-1 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span className="w-32">User</span>
                  <span className="w-28">Time</span>
                  <span className="flex-1">Question</span>
                </div>
                <div
                  className="custom-scrollbar flex-1 min-h-0 overflow-y-auto pr-2"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e1 #f1f5f9',
                  }}
                >
                  {webSearchQuestions.map((row) => (
                    <div
                      key={row.message_id}
                      className="flex items-start gap-4 border-t border-gray-100 px-1 py-3 text-sm text-gray-700"
                    >
                      <Tooltip title={`User ID: ${row.user_id}`} arrow>
                        <span className="w-32 cursor-default font-medium text-gray-900">
                          {row.username}
                        </span>
                      </Tooltip>
                      <span className="w-28 text-xs text-gray-500">{formatDateLabel(row.question_timestamp)}</span>
                      <Tooltip title={`Message ID: ${row.message_id}`} arrow>
                        <span className="flex-1 cursor-default text-gray-600">{row.question}</span>
                      </Tooltip>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

export default QuestionAnalysisTab;

const normalizePercent = (value?: number | null): number | null => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return null;
  }
  let normalized = value;
  if (Math.abs(normalized) <= 1) {
    normalized *= 100;
  }
  normalized = Math.min(Math.max(normalized, 0), 100);
  return Math.round(normalized * 10) / 10;
};

const calculateComplementPercent = (value?: number | null): number | null => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return null;
  }
  let ratio = value;
  if (ratio > 1) {
    ratio /= 100;
  }
  ratio = Math.min(Math.max(ratio, 0), 1);
  const percent = (1 - ratio) * 100;
  return Math.round(percent * 10) / 10;
};

const formatPercentLabel = (value: number | null): string =>
  value === null ? '--' : `${value.toFixed(1)}%`;

const formatCountLabel = (value?: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '--';
  }
  return Math.round(value).toLocaleString();
};

const formatDateLabel = (timestamp?: string | null): string => {
  if (!timestamp) {
    return '--';
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return '--';
  }
  return date.toISOString().slice(0, 10);
};